import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [status, setStatus] = useState("checking");
  const sessionId = params.get("session_id");
  const kind = params.get("kind");
  const adId = params.get("ad_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const pendingServiceSession = localStorage.getItem("pending_service_session");
    const isServicePurchase = kind === "service_purchase" || pendingServiceSession === sessionId;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        if (isServicePurchase) {
          const res = await axios.get(`${API}/services/purchase/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            localStorage.removeItem("pending_service_session");
            setStatus("paid");
            return;
          }
          if (attempts < 10) return setTimeout(poll, 2000);
          setStatus("pending");
          return;
        }
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          if (kind === "boost" && adId) {
            try { await axios.post(`${API}/ads/${adId}/boost/apply`, { session_id: sessionId }); }
            catch (err) { console.error("[PaymentSuccess] boost apply failed", err); }
          } else if (kind === "wallet_deposit") {
            try { await axios.post(`${API}/wallet/deposit/apply`, { session_id: sessionId }); }
            catch (err) { console.error("[PaymentSuccess] wallet deposit apply failed", err); }
          } else if (kind === "investor_deposit") {
            try { await axios.post(`${API}/investor/deposit/apply`, { session_id: sessionId }); }
            catch (err) { console.error("[PaymentSuccess] investor deposit apply failed", err); }
          }
          setStatus("paid");
        } else if (attempts < 10) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch { setStatus("error"); }
    };
    poll();
  }, [sessionId, kind, adId]);

  const goBack = () => {
    const pendingServiceSession = localStorage.getItem("pending_service_session");
    if (kind === "service_purchase" || pendingServiceSession === sessionId) { localStorage.removeItem("pending_service_session"); navigate("/services"); return; }
    if (kind === "boost") navigate("/ads");
    else if (kind === "wallet_deposit" || kind === "investor_deposit") navigate("/wallet");
    else navigate("/feed");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="p-8 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold mb-2">{t("paymentPending")}</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">{t("paymentSuccess")}</h1>
            {kind === "boost" && <p className="text-muted-foreground mb-4">Seu anúncio foi impulsionado! 🚀</p>}
            {kind !== "boost" && <p className="text-muted-foreground mb-6">Obrigado!</p>}
          </>
        )}
        <Button data-testid="back-home" onClick={goBack} className="rounded-full">Voltar</Button>
      </Card>
    </div>
  );
}
